import React, {useCallback, useEffect, useState} from 'react';
import {View, ScrollView, Image, Platform} from 'react-native';
import {StyleSheet} from 'react-native';

import {RNEButton, RNEIconButton, SVGIcon} from '../../components';
import {LogoLight} from './../../images/Image';
import {TouchableWithoutFeedback} from 'react-native-gesture-handler';
import {colorPalette} from '../../../core/config/color.config';
import {
  useGetMedia,
  useGetMediaUrl,
} from '../../server/react-query/useConversation';
import {useToast} from 'react-native-fast-toast';
import {usePullDownRefreshContext} from '../../stores/pullDownRefreshStore';
import {useFocusEffect} from '@react-navigation/native';

export interface ViewImageAttachmentView {
  subject: string;
  mediaId: string;
  onClose: () => void;
}

const ViewImageAttachmentView = (props: ViewImageAttachmentView) => {
  const toast = useToast();

  const [imageURL, setImageURL] = useState('');
  const {setCanRefresh} = usePullDownRefreshContext();

  const {data: imageData} = useGetMedia({
    subject: props.subject,
    mediaId: props.mediaId,
  });

  const {data, refetch} = useGetMediaUrl({
    subject: props.subject,
    mediaId: props.mediaId,
  });

  const onDownloadClick = () => {
    // toast?.show('Downloading...', {
    //   type: 'normal',
    //   id: props.mediaId,
    // });
    // downloadResource(
    //   data?.url!,
    //   imageData?.content.title ? imageData?.content.title : 'Image',
    // );
  };

  const downloadImage = (blob: any, filename: string) => {
    var a = document.createElement('a');
    a.download = filename;
    a.href = blob;
    document.body.appendChild(a);
    a.click();
    a.remove();
    // toast?.show('Download complete.', {
    //   type: 'success',
    //   id: props.mediaId,
    // });
  };

  const downloadResource = (url: string, filename: string) => {
    fetch(url)
      .then(response => response.blob())
      .then(blob => {
        let blobUrl = window.URL.createObjectURL(blob);
        downloadImage(blobUrl, filename);
      })
      .catch(e => console.error(e));
  };

  useEffect(() => {
    if (data?.url) {
      const index = data?.url.indexOf('?');
      const url = data?.url.substring(0, index);
      setImageURL(url);
    }
  }, [data]);

  const [scrollViewHeight, setScrollViewHeight] = useState(0);
  const [contentHeight, setContentHeight] = useState(0);
  const [scrollY, setScrollY] = useState(0);

  useFocusEffect(
    useCallback(() => {
      if (scrollViewHeight > 0 && contentHeight > 0) {
        const isScrollable = contentHeight > scrollViewHeight;

        if (!isScrollable) {
          setCanRefresh(true); // always allow pull-down if not scrollable
        } else {
          setCanRefresh(scrollY <= 0); // only allow when scrolled to top
        }
      }
    }, [scrollViewHeight, contentHeight, scrollY, setCanRefresh]),
  );
  const handleScroll = (e: any) => {
    const y = e.nativeEvent.contentOffset.y;
    setScrollY(y);

    const isScrollable = contentHeight > scrollViewHeight;

    if (!isScrollable) {
      setCanRefresh(true); // still always true if no scroll
    } else {
      setCanRefresh(y <= 0); // only true when back at the very top
    }
  };

  return (
    <View style={webStyles.mainContainer}>
      <View style={webStyles.headerContainer}>
        <Image style={webStyles.logo} source={LogoLight} />
        {Platform.OS === 'web' ? (
          <View style={webStyles.rightIcons}>
            <TouchableWithoutFeedback>
              <RNEIconButton
                icon={<SVGIcon name="download-down" size={28} />}
                type="clear"
                size={28}
                onPress={onDownloadClick}
                containerStyle={{borderRadius: 0}}
              />
            </TouchableWithoutFeedback>
            <TouchableWithoutFeedback>
              <RNEIconButton
                icon={<SVGIcon name="close-gray-71" size={36} />}
                type="clear"
                size={36}
                onPress={props.onClose}
              />
            </TouchableWithoutFeedback>
          </View>
        ) : (
          <View style={webStyles.rightIcons}>
            <TouchableWithoutFeedback onPress={onDownloadClick}>
              <View accessibilityRole="button">
                <SVGIcon name="download-down" size={28} />
              </View>
            </TouchableWithoutFeedback>
            <TouchableWithoutFeedback onPress={props.onClose}>
              <View style={webStyles.closeIcon} accessibilityRole="button">
                <SVGIcon name="close-gray-71" size={36} />
              </View>
            </TouchableWithoutFeedback>
          </View>
        )}
      </View>
      <ScrollView
        onScroll={handleScroll}
        onLayout={e => {
          setScrollViewHeight(e.nativeEvent.layout.height);
        }}
        onContentSizeChange={(contentWidth, contentHeight) => {
          setContentHeight(contentHeight);
        }}>
        <View style={webStyles.bodyContainer}>
          {imageURL.length > 0 ? (
            <View style={webStyles.imageContainer}>
              <Image
                source={{uri: imageURL}}
                style={webStyles.imageStyle}
                resizeMode="contain"
              />
            </View>
          ) : (
            <View style={webStyles.spinnerContainer}>
              <RNEButton
                height={20}
                loadingProps={loadingPropsStyle}
                loading={imageURL.length === 0}
              />
            </View>
          )}
        </View>
      </ScrollView>
    </View>
  );
};

export default ViewImageAttachmentView;

const loadingPropsStyle = {
  color: colorPalette.primary,
};

const webStyles = StyleSheet.create({
  mainContainer: {
    marginHorizontal: 40,
    marginVertical: 20,
    flex: 1,
    width: '40vw',
    maxWidth: 700,
    minWidth: 440,
    maxHeight: '70vh',
  },
  headerContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    borderBottomColor: 'rgba(229, 229, 229, 0.7)',
    borderBottomWidth: 1,
    paddingTop: 12,
    paddingBottom: 28,
  },
  logo: {
    width: 200,
    height: 44,
  },
  rightIcons: {
    flexDirection: 'row',
    alignSelf: 'center',
    justifyContent: 'space-between',
    width: 76,
  },
  closeIcon: {
    marginLeft: 28,
  },
  bodyContainer: {
    marginVertical: 40,
    marginHorizontal: 60,
  },
  spinnerContainer: {
    alignSelf: 'center',
  },
  imageContainer: {
    width: '100%',
    height: '100%',
    justifyContent: 'center',
  },
  imageStyle: {
    justifyContent: 'center',
    height: 550,
  },
});

const useMobileStyles = StyleSheet.create({
  mainContainer: {
    width: '100%',
    flex: 1,
    paddingHorizontal: 52,
    paddingVertical: 40,
  },
  spinnerContainer: {
    alignSelf: 'center',
  },
  imageContainer: {
    width: '100%',
    height: '100%',
    justifyContent: 'center',
  },
  imageStyle: {
    justifyContent: 'center',
    height: 550,
  },
});
